import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

/**
 * DialogContent
 *
 * Desktop: centered card with max-w-lg + zoom animation.
 * Mobile (< md): full-screen sheet that is a SINGLE vertical scroll container.
 *
 * Mobile design rules (do not regress these — they fix real iOS bugs):
 *   1. NO React.cloneElement on children. We previously hoisted DialogHeader /
 *      DialogFooter out of <form> wrappers via cloneElement on every render —
 *      that triggered React reconciliation drift on every keystroke and broke
 *      typing inside the form (ghost characters, focus loss).
 *   2. NO `touch-action: pan-y` on the container. iOS Safari interprets that
 *      as "no taps allowed below the first y-pan", which suppresses the click
 *      event that focuses inputs and opens Radix Select popovers.
 *   3. Header and footer pin to the viewport via `position: sticky`. The whole
 *      DialogContent is one big `overflow-y-auto` so the keyboard can push the
 *      focused input into view without us fighting the browser.
 *   4. `overscroll-behavior: contain` is enough to stop the underlying page
 *      from rubber-banding when the user swipes inside the form.
 */
const DialogContent = React.forwardRef(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        // Desktop: centered card with max-w-lg + zoom animation
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        // Mobile (< md): full-screen sheet, single overflow-y-auto. Header /
        // footer pin via position:sticky. We give body content automatic
        // px-4 via the [&>*:not([data-dialog-section])] selector so consumer
        // forms don't have to duplicate horizontal padding.
        "max-md:!inset-0 max-md:!left-0 max-md:!top-0 max-md:!translate-x-0 max-md:!translate-y-0 max-md:!w-screen max-md:!max-w-none max-md:!h-[100dvh] max-md:!max-h-[100dvh] max-md:!rounded-none max-md:!gap-0 max-md:!p-0 max-md:!block max-md:!overflow-y-auto max-md:overscroll-contain max-md:[&>*:not([data-dialog-section])]:px-4 max-md:data-[state=open]:slide-in-from-bottom max-md:data-[state=closed]:slide-out-to-bottom max-md:data-[state=open]:zoom-in-100 max-md:data-[state=closed]:zoom-out-100",
        className,
      )}
      {...props}>
      {children}
      <DialogPrimitive.Close
        className="absolute right-4 top-4 max-md:right-3 max-md:top-3 max-md:p-2 max-md:bg-slate-100 max-md:dark:bg-slate-800 max-md:rounded-full rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground z-20">
        <X className="h-4 w-4 max-md:h-5 max-md:w-5" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    data-dialog-section="header"
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      // Mobile: sticky-top header. We let it ride above the body content.
      "max-md:sticky max-md:top-0 max-md:z-10 max-md:bg-background max-md:px-4 max-md:py-3 max-md:pr-12 max-md:border-b max-md:border-slate-200 max-md:dark:border-slate-700 max-md:pt-[max(env(safe-area-inset-top),12px)]",
      className,
    )}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    data-dialog-section="footer"
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      // Mobile: sticky-bottom footer with safe-area-aware bottom padding +
      // top border. Sticks within the single DialogContent scroll container
      // so the buttons are always reachable without us fighting the browser.
      "max-md:sticky max-md:bottom-0 max-md:z-10 max-md:bg-background max-md:px-4 max-md:py-3 max-md:gap-2 max-md:border-t max-md:border-slate-200 max-md:dark:border-slate-700 max-md:pb-[max(env(safe-area-inset-bottom),12px)] max-md:flex-row max-md:[&>*]:flex-1",
      className,
    )}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
